import React, { useEffect, useState } from 'react';
import axios from '../axiosConfig';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { filterBy } from '@progress/kendo-data-query';
import { orderBy } from '@progress/kendo-data-query';
import '@progress/kendo-theme-default/dist/all.scss';
import './GeneralGridStyle.scss';
import CustomLockedCell from './customizedColumns/CustomLockedCell';
import useProfileData from '../services/ProfileDataService';
import CustomFilter from '../components/customizedFilters/CustomFilter';
import Loader from './Loader';
import Toast from './ToastNotification';
import { useMsal } from '@azure/msal-react';
import Modal from './Modal';
import {
	ExcelExport,
	ExcelExportColumn,
} from '@progress/kendo-react-excel-export';
import LogOutService from '../services/LogOutService';
import SpecialTextCell from './customizedColumns/SpecialTextCell';
import { Window } from '@progress/kendo-react-dialogs';
import useDebounce from './../Utilities/useDebounce';
import {
	getCurrentLine,
	subscribeToCurrentLineChanges,
} from '../services/CurrentLineService';
import { useGetSelectionsByCodeNum } from '../Helpers/Hooks/UseGetSelectionsByCodeNum';
import { useReferenceOrdersData } from '../Helpers/Hooks/UseGetReferenceOrdersData';

const ReferenceOrdersGrid = ({
	columns = [],
	dateRange,
	filter,
	setFilter,
	prevFilter,
	setPrevFilter,
	currentFilter,
	setCurrentFilter,
	onRemoveAll,
	setRemoveFilter,
	dropdownFilters,
	setDropdownFilters,
	removeFilter,
	disabled,
	exportExcel,
	setExportExcel,
	modelTypes,
	onLoadColumnsInfo,
	setDisableColumnsSelector,
	selectedItems,
	setInitialFilterTitle
}) => {
	const [users, setUsers] = useState([]);	
	const [pageSize, setPageSize] = useState(50);
	const [sort, setSort] = useState([{ field: 'promiseDate', dir: 'desc' }]);
	const [page, setPage] = useState({ skip: 0, take: pageSize });
	const [currentLine, setCurrentLine] = useState(getCurrentLine().ProdFamilyId);
	const { token } = useProfileData(false);

	const [currentPage, setCurrentPage] = useState(1);
	const [totalRows, setTotalRows] = useState(0);

	const [toast, setToast] = useState(null);
	const [idtoast, setIdtoast] = useState(1);
	const { accounts, instance } = useMsal();
	const viewAlias = 4;
	const debouncedFilter = useDebounce(filter, '600');

	useEffect(() => {
		const unsubscribe = subscribeToCurrentLineChanges((newCurrentLine) => {
			setCurrentLine(newCurrentLine.ProdFamilyId);
		});
		return () => {
			unsubscribe();
		};
	}, []);

	const capitalizeFirstLetter = (string) => {
		return string?.charAt(0).toUpperCase() + string?.slice(1);
	};

	const filterObject = {
		logic: "or",
		filters: [],
	};

	const [filterValues, setFilterValues] = useState(null);
	const [modifiedFilter, setModifiedFilter] = useState(filterObject);

	const {
		data: referenceOrdersData,
		isLoading: loading,
		refetch,
	} = useReferenceOrdersData({
		currentLine,
		filterValues,
		modifiedFilter,
		sort,
		page,
		token,
		debouncedFilter,
		setToast,
	});

	useEffect(() => {		
	if (referenceOrdersData) {						
		setUsers(referenceOrdersData.items);
		setTotalRows(referenceOrdersData.totalRows);
	}
	}, [modifiedFilter, referenceOrdersData]);

	//Reset filters applied when changing line
	useEffect(() => {		
		setFilter(filterObject);
	}, [currentLine]);

	const fetchData = async () => {
		setToast({ showToast: false });
		setIdtoast(idtoast + 1);

		const filterValues = modelTypes
			.filter((item) => item.selected)
			.map((item) => item.value);
			
		setFilterValues(filterValues);

			let modifiedFilter = filter;

			if (filter) {
				modifiedFilter = {
					...filter,
					filters: filter.filters
						? filter.filters.map((filterItem) => {
								const filterOption = filterOptions.find(
									(option) => option.columnName === filterItem.field
								);
								if (filterOption) {
									return {
										...filterItem,
										CodeStart: filterOption.codeStart,
										CodeLength: filterOption.codeLength,
									};
								} else {
									return {
										...filterItem,
										field: capitalizeFirstLetter(filterItem.field),
										CodeStart: -1,
									};
								}
							})
						: [],
				};
			}

		setModifiedFilter(modifiedFilter);
		

	};

	useEffect(() => {
		setPageSize(page.take);
	}, [page]);

	useEffect(() => {
		setToast({ showToast: false });
		setIdtoast(idtoast + 1);

		if (modelTypes.some((option) => option.selected)) fetchData();
	}, [token, page.skip, page.take, sort, dateRange, debouncedFilter]);

	useEffect(() => {
		setPage({ skip: 0, take: pageSize });
		setCurrentPage(1);
	}, [dateRange, sort, debouncedFilter]);

	useEffect(() => {
		if (modelTypes.some((option) => option.selected)) fetchData();
	}, [modelTypes]);

	const [filtro, setFiltro] = useState(null);

	const [filterOptions, setFilterOptions] = useState([]);

	const commonFieldValues = [
		'Ship Date',
		'BOM Revision',
		'Order Number',
		'Status',
		'Qty',
		'ESOI Number',
		'Description',
		'Special Text',
		'Code',
	];

	const [selectedColumns, setSelectedColumns] = useState(columns);

	const { data: codeOptionsData, isLoading } = useGetSelectionsByCodeNum({
		currentLine,
		token,
	});

	useEffect(() => {
		if (codeOptionsData) {			
			const newColumns = [];

			setInitialFilterTitle(codeOptionsData[0].description);

			// Define columns values
			for (let i = 0; i < codeOptionsData.length; i++) {
				const field = codeOptionsData[i].description;

				const columnDataOptions = {
					field: field,
					title: (codeOptionsData[i]?.codes[0]?.codeItem)?.toString().padStart(2, '0') + ' ' + field, // start with 0
					filterType: 'dropdown',
					visible: true,
					minWidth: 80,
				};

				newColumns.push(columnDataOptions);
			}

			//final object used to display columns with their options in the dropdown
			const allResponses = codeOptionsData.map((group, index) => ({
				columnName: newColumns[index]?.field || group.description, 
				uniqueValues: group.codes.map((code) => code.value), 
				codeStart: group.codes[0]?.position,
				codeLength: group.codes[0]?.length, 
			}));
			

			setFilterOptions(allResponses);
			//Map dinamically the codeItemsColumns
			const updatedColumns = [...columns.slice(0, 9), ...newColumns];
			onLoadColumnsInfo(updatedColumns);
			setDisableColumnsSelector(false);

			//Apply filters sent from code split view
			setFilter((prevFilter) => {
				const newFilter = prevFilter
					? { ...prevFilter }
					: { logic: 'or', filters: [] };

				const existingFields = new Set(
					newFilter.filters.map((f) => f.field + f.value)
				);

				//create filters object
				selectedItems.forEach((item) => {
					if (
						item.selected &&
						!existingFields.has(item.Description + item.Value)
					) {
						newFilter.filters.push({
							field: item.Description,
							operator: 'contains',
							value: item.Value,
						});
					}
				});

				return newFilter;
			});

			const currentFilterField =
				selectedItems.length > 0 ? selectedItems[0].Description : null;

			if (currentFilterField) {
				const selectedOptionsForField = selectedItems
					.filter((item) => item.Description === currentFilterField)
					.map((item) => item.Value);

				setFiltro(currentFilterField);
				setCurrentFilter(selectedOptionsForField);
				setRemoveFilter(false);

				setFilter((filter) => {
					const allFields =
						filter?.filters?.map((filter) => filter.field) || [];
					allFields.unshift(currentFilterField);
					const uniqueFields = [...new Set(allFields)];

					setPrevFilter(uniqueFields);
					setDropdownFilters(filter?.filters);

					return filter;
				});
			}			
		}
	}, [codeOptionsData]);

	const filterConfig = {
		text: 'text',
		number: 'numeric',
		boolean: 'boolean',
		date: 'date',
		dropdown: {
			filterCell: (props) => {
				const { field } = props;
				return (
					<CustomFilter
						{...props}
						data={users}
						field={field}
						currentFilterField={field}
						onSelectionChange={handleCustomFilterChange}
						onRemoveAll={onRemoveAll}
						selectedOptions2={currentFilter}
						selectedFilter={filtro}
						removeFilter={removeFilter}
						prevFilter={prevFilter}
						filter={dropdownFilters}
						viewOption={viewAlias} //referenceOrders
						endPointData={codeOptionsData}
					/>
				);
			},
		},
	};

	const handleCustomFilterChange = (selectedOptions, currentFilterField) => {
		setFilter((prevFilter) => {
			if (!prevFilter) {
				// No filter exist
				return {
					logic: 'or',
					filters: [
						...selectedOptions.map((option) => ({
							field: currentFilterField,
							operator: 'contains',
							value: option,
						})),
					],
				};
			} else {
				// if already exist a filter
				const updatedFilters = [
					...prevFilter.filters.filter(
						(filter) => filter.field !== currentFilterField
					),
					...selectedOptions.map((option) => ({
						field: currentFilterField,
						operator: 'contains',
						value: option,
					})),
				];

				return {
					...prevFilter,
					filters: updatedFilters,
				};
			}
		});
		setFiltro(currentFilterField);
		setCurrentFilter(selectedOptions);
		setRemoveFilter(false);
		const allFields = filter?.filters?.map((filter) => filter.field) || [];
		allFields.unshift(currentFilterField);
		const allFields2 = [...new Set(allFields)];
		setPrevFilter(allFields2);
		setDropdownFilters(filter?.filters);
	};

	const pageChange = (event) => {
		const targetEvent = event.targetEvent;
		const newPage = {
			skip: event.page.skip,
			take: targetEvent.value === 'All' ? totalRows : event.page.take,
		};

		setPage(newPage);
		setCurrentPage(newPage.skip / pageSize + 1);
	};

	const handleFilterChange = (event) => {
		setFilter(event.filter);
	};

	const filteredUsers = filterBy(users, filter);
	const sortedUsers = orderBy(filteredUsers, sort);

	useEffect(
		() => {
			setSelectedColumns(columns);
		},
		[columns],
		[sortedUsers]
	);

	const [selectedColumnFields, setSelectedColumnFields] = useState(
		columns.map((column) => column.field)
	);

	useEffect(() => {
		setSelectedColumnFields(columns.map((column) => column.field));
	}, [columns]);

	const [profileImgAssigned, setProfileImgAssigned] = useState();
	const [designerName, setName] = useState();
	const [modalVisible, setModalVisible] = useState(false);

	const handleSortChange = (e) => {
		setSort(e.sort || []);
	};

	const _export = React.useRef(null);
	const _grid = React.useRef();

	const _formattedDate = (() => {
		const today = new Date();
		const month = (today.getMonth() + 1).toString().padStart(2, '0');
		const day = today.getDate().toString().padStart(2, '0');
		const year = today.getFullYear();
		return `${month}-${day}-${year}`;
	})();

	const excelExport = () => {
		if (_export.current !== null && exportExcel === true) {
			_export.current.save(users);			
		}
	};

	useEffect(() => {
		excelExport();
		setExportExcel(false);
	}, [exportExcel === true]);

	// Zoom adjustment
	const [zoomLevel, setZoomLevel] = useState(1);
	const [gridHeight, setGridHeight] = useState(window.innerHeight - 270);

	useEffect(() => {
		const handleResize = () => {
			const viewportWidth = window.innerWidth;
			const windowWidth = window.outerWidth;
			const newZoomLevel = windowWidth / viewportWidth;
			setZoomLevel(newZoomLevel);

			const newGridHeight = window.innerHeight - 270;
			setGridHeight(newGridHeight);
		};

		handleResize();
		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, []);
	// Zoom adjustment

	return (
		<div style={{ position: 'relative' }}>
			{(loading || isLoading) && <Loader></Loader>}

			<Modal
				visible={modalVisible}
				onClose={() => setModalVisible(false)}
				title="Oops! It looks like this task was already taken"
				message={designerName + ' took this task'}
				type="TAKENORDER"
				image={profileImgAssigned}
			/>

			{toast && (
				<Toast
					key={idtoast}
					showToast={toast.showToast}
					message={toast.message}
					type={toast.type}
				/>
			)}

			<div
				style={{
					height: gridHeight,
					marginBottom: '15px',
					position: 'relative',
				}}
			>
				{/* TODO: It will be necessary to create a single Grid to unify its functionality */}
				<ExcelExport
					ref={_export}
					fileName={`ReferenceOrders_${_formattedDate}.xlsx`}
				>
					<Grid
						className="roboto-15-400"
						data={users || []}
						skip={page.skip}
						take={page.take}
						total={totalRows}
						pageable={{
							buttonCount: 4,
							type: 'input',
							pageSizes: [50, 150, 200].filter((num) => num <= totalRows),
							pageSize: pageSize,
						}}
						onPageChange={pageChange}
						sortable={true}
						filterable={true}
						filter={filter}
						onFilterChange={handleFilterChange}
						resizable
						reorderable
						sort={sort}
						onSortChange={handleSortChange}
						style={{
							borderRadius: '8px',
							display: 'flex',
							margin: '15px',
							height: 'inherit',
						}}
						ref={_grid}
					>
						<GridColumn
							className="roboto-15-400"
							reorderable={false}
							sortable={false}
							filterable={false}
							editable={false}
							field="rowIndex"
							title="#"
							width="40px"
						/>
						{selectedColumns
							.filter((column) => column.visible)
							.map((column) => {
								if (column.title === 'Special Text') {
									return (
										<GridColumn
											className="roboto-15-400"
											sortable={false}
											key={column.field}
											field={column.field}
											title={column.title}
											headerCell={column.header}
											width={column.minWidth}
											filterable={false} // change to true after handling this filter on the back end
											// cell={column.cell}
											cell={(props) => <SpecialTextCell {...props} />}
										/>
									);
								} else if (commonFieldValues.includes(column.title) || column.title.startsWith("01 ")) {
									return (
										<GridColumn
											className="roboto-15-400"
											sortable={false}
											key={column.field}
											field={column.field}
											title={column.title}
											headerCell={column.header}
											width={column.minWidth}
											filterable={false}
											cell={column.cell}
										/>
									);
								} else {
									return (
										<GridColumn
											className="roboto-15-400"
											sortable={false}
											key={column.field}
											field={column.field}
											title={column.title}
											headerCell={column.header}
											width={column.minWidth}
											filter={filterConfig[column.filterType]}
											format="{0:d}"
											filterCell={filterConfig[column.filterType].filterCell}
											cell={column.cell}
										/>
									);
								}
							})}
					</Grid>
				</ExcelExport>
			</div>
		</div>
	);
};

export default ReferenceOrdersGrid;
